import { useQueries, useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { queryCodeDirectories } from '@/features/codeCoverage/data/codeDirectories';
import { queryCoverageFileTree } from '@/features/codeCoverage/data/coverageFiles';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import {
  DAILY_STATUS_ROW_HEIGHT,
  HEADER_SEPARATOR_SX,
  HEADER_SUBTEXT_SX,
  HEADER_TEXT_SX,
  resolveHistoryBodyMinHeight,
} from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.constants';
import { EntityDailyHistoryPanelHeader } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanelHeader';
import { TABLE_GRID_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { buildToolbarIconButtonSx } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { queryKeys } from '@/shared/hooks';
import { r, s } from '@/theme/tokens';
import { ChartEmptyState, CHART_EMPTY_STATE_FILTER_HINT, Panel } from '@/ui/composites';
import {
  buildInFilter,
  buildProjectScopedFilters,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  Tooltip,
} from '@/ui/primitives';
import { CloseFullscreenIcon, OpenInFullIcon } from '@/ui/primitives/icons';
import {
  type CoverageHistoryGroupRow,
  type CoverageHistoryTreeNode,
  CoverageRunsHistoryRows,
} from './CoverageRunsHistoryTableRows';
import type { CoverageFileTreeNodeDto, QueryFilter } from '@flowqa/shared';

type CoverageRunsHistoryTableProps = {
  dateRangeLabel?: string | null;
  endDay: string | null;
  filters: QueryFilter[];
  loading?: boolean;
  organisationId?: string | null;
  projectId?: string | null;
  queryKeyScope: string;
  startDay: string | null;
};

const HEADER_CELL_BASE_SX = {
  px: s('md'),
  borderBottom: 'none',
  backgroundColor: 'background.paper',
  pt: s('md'),
  pb: s('none'),
  verticalAlign: 'top',
} as const;
const BODY_CELL_SX = {
  px: s('md'),
  py: s('none'),
  borderBottom: 'none',
  minWidth: 0,
  verticalAlign: 'middle',
} as const;
const LOADING_BODY_CELL_SX = {
  pt: s('md'),
  verticalAlign: 'top',
} as const;
const TOOLBAR_CONTROL_SIZE = 18;
const DIRECTORY_ROW_PREFIX = 'directory:';

const isDirectoryRowId = (rowId: string): boolean => rowId.startsWith(DIRECTORY_ROW_PREFIX);

const rowPathFromId = (rowId: string): string => rowId.slice(rowId.indexOf(':') + 1);

const toTreeNode = (
  node: CoverageFileTreeNodeDto,
  childItemsByPath: ReadonlyMap<string, CoverageFileTreeNodeDto[]>,
  expandedRowIds: ReadonlySet<string>,
  loadingPaths: ReadonlySet<string>,
): CoverageHistoryTreeNode => ({
  branchesPct: node.branchesPct ?? null,
  children:
    node.hasChildren && expandedRowIds.has(node.id)
      ? (childItemsByPath.get(node.path) ?? []).map((child) =>
          toTreeNode(child, childItemsByPath, expandedRowIds, loadingPaths),
        )
      : [],
  fileId: node.fileId,
  functionsPct: node.functionsPct ?? null,
  hasChildren: node.hasChildren,
  id: node.id,
  isLoadingChildren: loadingPaths.has(node.path),
  linesPct: node.linesPct,
  name: node.name,
  path: node.path,
  statementsPct: node.statementsPct ?? null,
  type: node.type,
});

const buildGroupRows = (
  nodes: CoverageFileTreeNodeDto[],
  childItemsByPath: ReadonlyMap<string, CoverageFileTreeNodeDto[]>,
  expandedRowIds: ReadonlySet<string>,
  loadingPaths: ReadonlySet<string>,
): CoverageHistoryGroupRow[] =>
  nodes.map((node) => ({
    branchesPct: node.branchesPct ?? null,
    children:
      node.hasChildren && expandedRowIds.has(node.id)
        ? (childItemsByPath.get(node.path) ?? []).map((child) =>
            toTreeNode(child, childItemsByPath, expandedRowIds, loadingPaths),
          )
        : [],
    functionsPct: node.functionsPct ?? null,
    hasChildren: node.hasChildren,
    id: node.id,
    isLoadingChildren: loadingPaths.has(node.path),
    linesPct: node.linesPct,
    name: node.name,
    path: node.path,
    statementsPct: node.statementsPct ?? null,
  }));

const collectExpandableRowIds = (
  rows: Array<CoverageHistoryGroupRow | CoverageHistoryTreeNode>,
): string[] =>
  rows.flatMap((row) => {
    if (!row.hasChildren) {
      return [];
    }

    return [row.id, ...collectExpandableRowIds(row.children)];
  });

const HISTORY_LOADING_ROW_COUNT = 5;

const renderLoadingRows = (): ReactElement => (
  <>
    {Array.from({ length: HISTORY_LOADING_ROW_COUNT }, (_unused, index) => (
      <TableRow
        key={`coverage-history-loading-${String(index)}`}
        sx={{ height: `${DAILY_STATUS_ROW_HEIGHT}px` }}
      >
        <TableCell sx={[BODY_CELL_SX, LOADING_BODY_CELL_SX]}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: '32px',
              minWidth: 0,
              width: '100%',
            }}
          >
            <Skeleton
              variant="rounded"
              width={index % 2 === 0 ? '68%' : '61%'}
              sx={{
                height: '14px',
                transform: 'none',
                borderRadius: r('pill'),
              }}
            />
            <Box
              sx={{
                ml: s('md'),
                width: s('2xl'),
                height: s('2xl'),
                flexShrink: 0,
              }}
            />
          </Box>
        </TableCell>
        {[0, 1, 2, 3].map((columnIndex) => (
          <TableCell
            key={`coverage-history-loading-${String(index)}-${String(columnIndex)}`}
            sx={[BODY_CELL_SX, LOADING_BODY_CELL_SX]}
          >
            <Skeleton
              variant="rounded"
              sx={{
                width: '100%',
                height: '14px',
                transform: 'none',
                borderRadius: r('sm'),
              }}
            />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

export const CoverageRunsHistoryTable = ({
  dateRangeLabel = null,
  endDay,
  filters,
  loading = false,
  organisationId = null,
  projectId = null,
  queryKeyScope,
  startDay,
}: CoverageRunsHistoryTableProps): ReactElement => {
  const emptyStateMinHeight = resolveHistoryBodyMinHeight({
    rowCount: 1,
    rowHeight: DAILY_STATUS_ROW_HEIGHT,
    headerHeight: TABLE_GRID_HEADER_HEIGHT + 14,
  });
  const { thresholds } = useProjectMetricThresholds(projectId);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => new Set());
  const [expandAllPending, setExpandAllPending] = useState(false);

  useEffect(() => {
    setExpandedRowIds(new Set());
    setExpandAllPending(false);
  }, [endDay, queryKeyScope, startDay]);

  const rootRowsQuery = useQuery({
    queryKey: queryKeys.coverageFiles(
      organisationId,
      projectId,
      `history-tree-root:${queryKeyScope}:${startDay ?? 'none'}:${endDay ?? 'none'}`,
    ),
    enabled: !loading,
    queryFn: async () =>
      await queryCoverageFileTree(
        {
          page: 1,
          pageSize: 1,
          filters,
          treePath: null,
        },
        organisationId,
      ),
  });
  const rootNodes = rootRowsQuery.data?.items ?? [];
  const expandedDirectoryPaths = useMemo(
    () =>
      [...expandedRowIds]
        .filter((rowId) => isDirectoryRowId(rowId))
        .map((rowId) => rowPathFromId(rowId))
        .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' })),
    [expandedRowIds],
  );
  const childQueries = useQueries({
    queries: expandedDirectoryPaths.map((treePath) => ({
      queryKey: queryKeys.coverageFiles(
        organisationId,
        projectId,
        `history-tree-child:${queryKeyScope}:${treePath}:${startDay ?? 'none'}:${endDay ?? 'none'}`,
      ),
      enabled: !loading,
      queryFn: async () =>
        await queryCoverageFileTree(
          {
            page: 1,
            pageSize: 1,
            filters,
            treePath,
          },
          organisationId,
        ),
    })),
  });
  const childItemsByPath = useMemo(() => {
    const nextMap = new Map<string, CoverageFileTreeNodeDto[]>();

    expandedDirectoryPaths.forEach((treePath, index) => {
      nextMap.set(treePath, childQueries[index]?.data?.items ?? []);
    });

    return nextMap;
  }, [childQueries, expandedDirectoryPaths]);
  const loadingPaths = useMemo(() => {
    const nextPaths = new Set<string>();

    expandedDirectoryPaths.forEach((treePath, index) => {
      if (childQueries[index]?.isLoading || childQueries[index]?.isFetching) {
        nextPaths.add(treePath);
      }
    });

    return nextPaths;
  }, [childQueries, expandedDirectoryPaths]);
  const loadedDirectoryPaths = useMemo(() => {
    const nextPaths = new Set<string>();

    rootNodes.forEach((node) => {
      if (node.type === 'directory') {
        nextPaths.add(node.path);
      }
    });

    childItemsByPath.forEach((nodes) => {
      nodes.forEach((node) => {
        if (node.type === 'directory') {
          nextPaths.add(node.path);
        }
      });
    });

    return [...nextPaths].sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' }),
    );
  }, [childItemsByPath, rootNodes]);
  const directoryLinksQuery = useQuery({
    queryKey: queryKeys.codeDirectories(
      organisationId,
      projectId,
      `history-tree-links:${loadedDirectoryPaths.join(',') || 'none'}`,
    ),
    enabled: !loading && loadedDirectoryPaths.length > 0,
    queryFn: async () =>
      await queryCodeDirectories(
        {
          page: 1,
          pageSize: Math.max(loadedDirectoryPaths.length, 1),
          filters: mergeFilters(
            buildProjectScopedFilters(projectId),
            buildInFilter('normalizedPath', loadedDirectoryPaths),
          ),
        },
        organisationId,
      ),
  });
  const directoryHrefByPath = useMemo(
    () =>
      new Map(
        (directoryLinksQuery.data?.items ?? []).map((directory) => [
          directory.normalizedPath,
          `/coverage-directories/${directory.id}`,
        ]),
      ),
    [directoryLinksQuery.data?.items],
  );
  const rows = useMemo(
    () => buildGroupRows(rootNodes, childItemsByPath, expandedRowIds, loadingPaths),
    [childItemsByPath, expandedRowIds, loadingPaths, rootNodes],
  );
  const expandableRowIds = useMemo(() => collectExpandableRowIds(rows), [rows]);
  const toolbarIconButtonSx = buildToolbarIconButtonSx('common.white', TOOLBAR_CONTROL_SIZE);
  const hasExpandableRows = expandableRowIds.length > 0;
  const canExpandAll =
    hasExpandableRows && expandableRowIds.some((rowId) => !expandedRowIds.has(rowId));
  const canCollapseAll = hasExpandableRows && expandedRowIds.size > 0;
  const showLoadingState = loading || rootRowsQuery.isLoading;
  const showEmptyState = rows.length === 0 && !showLoadingState;
  const allLoadedExpandableRowsExpanded =
    hasExpandableRows && expandableRowIds.every((rowId) => expandedRowIds.has(rowId));

  const toggleRow = (rowId: string): void => {
    setExpandedRowIds((current) => {
      const next = new Set(current);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
    setExpandAllPending(false);
  };

  const expandAllRows = (): void => {
    setExpandAllPending(true);
  };

  const collapseAllRows = (): void => {
    setExpandedRowIds(new Set());
    setExpandAllPending(false);
  };

  useEffect(() => {
    if (!expandAllPending) {
      return;
    }

    setExpandedRowIds((current) => {
      const next = new Set(current);

      expandableRowIds.forEach((rowId) => {
        if (!next.has(rowId)) {
          next.add(rowId);
        }
      });

      return next.size === current.size ? current : next;
    });

    if (allLoadedExpandableRowsExpanded && loadingPaths.size === 0 && !rootRowsQuery.isFetching) {
      setExpandAllPending(false);
    }
  }, [
    allLoadedExpandableRowsExpanded,
    expandAllPending,
    expandableRowIds,
    loadingPaths,
    rootRowsQuery.isFetching,
  ]);

  const expandCollapseControls = hasExpandableRows ? (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        width: 'fit-content',
      }}
    >
      <Tooltip title="Expand all" placement="top" arrow>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton
            aria-label="Expand all directories"
            onClick={expandAllRows}
            disabled={!canExpandAll}
            sx={[
              toolbarIconButtonSx,
              {
                p: s('none'),
                minWidth: TOOLBAR_CONTROL_SIZE,
                maxWidth: TOOLBAR_CONTROL_SIZE,
                width: TOOLBAR_CONTROL_SIZE,
                height: TOOLBAR_CONTROL_SIZE,
              },
              !canExpandAll ? { opacity: 0.5 } : null,
            ]}
          >
            <OpenInFullIcon
              sx={{
                width: `${TOOLBAR_CONTROL_SIZE}px !important`,
                height: `${TOOLBAR_CONTROL_SIZE}px !important`,
              }}
            />
          </IconButton>
        </Box>
      </Tooltip>
      <Tooltip title="Collapse all" placement="top" arrow>
        <Box component="span" sx={{ display: 'inline-flex', ml: s('none') }}>
          <IconButton
            aria-label="Collapse all directories"
            onClick={collapseAllRows}
            disabled={!canCollapseAll}
            sx={[
              toolbarIconButtonSx,
              {
                p: s('none'),
                minWidth: TOOLBAR_CONTROL_SIZE,
                maxWidth: TOOLBAR_CONTROL_SIZE,
                width: TOOLBAR_CONTROL_SIZE,
                height: TOOLBAR_CONTROL_SIZE,
              },
              !canCollapseAll ? { opacity: 0.5 } : null,
            ]}
          >
            <CloseFullscreenIcon
              sx={{
                width: `${TOOLBAR_CONTROL_SIZE}px !important`,
                height: `${TOOLBAR_CONTROL_SIZE}px !important`,
              }}
            />
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  ) : null;

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="none"
      headerGap="none"
      headerSlot={
        <EntityDailyHistoryPanelHeader
          title="Directory / File Coverage"
          dateRangeLabel={dateRangeLabel}
          extraToolbarContent={null}
          showSortControl={false}
          showResetControl={false}
          loading={showLoadingState}
          canInteractWithToolbar={false}
          hasActiveSort={false}
          hoverTarget={null}
          toolbarControlSize={TOOLBAR_CONTROL_SIZE}
          toolbarIconButtonSx={toolbarIconButtonSx}
          toolbarInactiveIconColor="common.white"
          onSortButtonClick={() => undefined}
          onReset={() => undefined}
          onHoverTargetChange={() => undefined}
        />
      }
      sx={{ backgroundColor: 'background.paper', py: s('none'), px: s('md') }}
    >
      {showLoadingState ? (
        <Box>
          <Table
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableBody-root > .MuiTableRow-root:last-of-type > .MuiTableCell-root': {
                pb: s('sm'),
              },
            }}
          >
            <colgroup>
              <col width="32%" />
              <col width="17%" />
              <col width="17%" />
              <col width="17%" />
              <col width="17%" />
            </colgroup>
            <TableHead>
              <TableRow
                sx={{
                  height: `${TABLE_GRID_HEADER_HEIGHT + 14}px`,
                  position: 'relative',
                  '&::after': buildTableDividerPseudoSx('bottom'),
                  '& > .MuiTableCell-root': { borderBottom: 'none' },
                }}
              >
                <TableCell
                  sx={[
                    {
                      position: 'relative',
                      width: '32%',
                    },
                    HEADER_CELL_BASE_SX,
                    HEADER_SEPARATOR_SX,
                  ]}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="sm"
                    sx={{ minWidth: 0, width: '100%' }}
                  >
                    <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>
                      Directory
                    </Text>
                  </Stack>
                </TableCell>
                <TableCell colSpan={4} sx={[HEADER_CELL_BASE_SX]}>
                  <Stack gap="sm" sx={{ minWidth: 0 }}>
                    <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>
                      Coverage
                    </Text>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                        columnGap: s('md'),
                        alignItems: 'center',
                        width: '100%',
                        minWidth: 0,
                        pb: s('none'),
                      }}
                    >
                      {['Line', 'Function', 'Branch', 'Statement'].map((label) => (
                        <Text
                          key={label}
                          role="body"
                          sx={[
                            HEADER_SUBTEXT_SX,
                            {
                              fontSize: (muiTheme) => muiTheme.typography.pxToRem(15),
                              color: 'text.secondary',
                            },
                          ]}
                        >
                          {label}
                        </Text>
                      ))}
                    </Box>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ height: s('sm') }}>
                <TableCell colSpan={5} sx={{ p: s('none'), borderBottom: 'none' }} />
              </TableRow>
              {renderLoadingRows()}
            </TableBody>
          </Table>
          <Box
            sx={{
              height: s('sm'),
            }}
          />
        </Box>
      ) : showEmptyState ? (
        <Box
          sx={{
            px: s('md'),
            pb: s('sm'),
            minHeight: emptyStateMinHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChartEmptyState
            title="No directory or file coverage found."
            body={CHART_EMPTY_STATE_FILTER_HINT}
          />
        </Box>
      ) : (
        <Box>
          <Table
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableBody-root > .MuiTableRow-root:last-of-type > .MuiTableCell-root': {
                pb: s('sm'),
              },
            }}
          >
            <colgroup>
              <col width="32%" />
              <col width="17%" />
              <col width="17%" />
              <col width="17%" />
              <col width="17%" />
            </colgroup>
            <TableHead>
              <TableRow
                sx={{
                  height: `${TABLE_GRID_HEADER_HEIGHT + 14}px`,
                  position: 'relative',
                  '&::after': buildTableDividerPseudoSx('bottom'),
                  '& > .MuiTableCell-root': { borderBottom: 'none' },
                }}
              >
                <TableCell
                  sx={[
                    {
                      position: 'relative',
                      width: '32%',
                    },
                    HEADER_CELL_BASE_SX,
                    HEADER_SEPARATOR_SX,
                  ]}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="sm"
                    sx={{ minWidth: 0, width: '100%' }}
                  >
                    <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>
                      Directory
                    </Text>
                    {expandCollapseControls}
                  </Stack>
                </TableCell>
                <TableCell colSpan={4} sx={[HEADER_CELL_BASE_SX]}>
                  <Stack gap="sm" sx={{ minWidth: 0 }}>
                    <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>
                      Coverage
                    </Text>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                        columnGap: s('md'),
                        alignItems: 'center',
                        width: '100%',
                        minWidth: 0,
                        pb: s('none'),
                      }}
                    >
                      {['Line', 'Function', 'Branch', 'Statement'].map((label) => (
                        <Text
                          key={label}
                          role="body"
                          sx={[
                            HEADER_SUBTEXT_SX,
                            {
                              fontSize: (muiTheme) => muiTheme.typography.pxToRem(15),
                              color: 'text.secondary',
                            },
                          ]}
                        >
                          {label}
                        </Text>
                      ))}
                    </Box>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ height: s('sm') }}>
                <TableCell colSpan={5} sx={{ p: s('none'), borderBottom: 'none' }} />
              </TableRow>
              <CoverageRunsHistoryRows
                ancestorHasNextSiblings={[]}
                directoryThresholds={thresholds.directories}
                directoryHrefByPath={directoryHrefByPath}
                depth={0}
                expandedRowIds={expandedRowIds}
                fileThresholds={thresholds.files}
                onToggleRow={toggleRow}
                rows={rows}
              />
            </TableBody>
          </Table>
          <Box
            sx={{
              height: s('sm'),
            }}
          />
        </Box>
      )}
    </Panel>
  );
};
